'use client';

import Image from 'next/image';
import Link from 'next/link';

import { useTranslations } from '@/components/language-provider';

function CheckIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true" {...props}>
      <path d="M4 10.5 8.5 15 16 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function FoundingOrganizersPage() {
  const { messages } = useTranslations();
  const content = messages.foundingOrganizers;

  return (
    <div className="space-y-16">
      <section className="grid gap-10 rounded-3xl border border-border/60 bg-surface/90 p-10 shadow-xl shadow-primary/10 backdrop-blur lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
        <div className="space-y-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-muted/40 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <span className="size-2 rounded-full bg-primary" aria-hidden="true" />
            {content.hero.eyebrow}
          </span>
          <h1 className="text-balance text-4xl font-bold tracking-tight text-foreground sm:text-5xl">
            {content.hero.title}
          </h1>
          <p className="rounded-2xl bg-primary/10 px-4 py-3 text-sm font-semibold text-primary">
            {content.hero.highlight}
          </p>
          <p className="text-lg text-muted-foreground">{content.hero.description}</p>
          <div className="flex flex-wrap items-center gap-4">
            {content.hero.badges.map((badge) => (
              <Link
                key={badge.id}
                href={badge.href}
                className="inline-flex"
                target="_blank"
                rel="noreferrer"
              >
                <Image
                  src={badge.image}
                  alt={badge.label}
                  width={badge.width}
                  height={badge.height}
                  className={badge.className ?? 'h-12 w-auto'}
                  unoptimized
                />
              </Link>
            ))}
          </div>
          <div>
            <Link
              href={content.hero.primaryCtaHref}
              className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-primary to-primary/80 px-6 py-3 text-sm font-semibold btn-primary shadow-lg shadow-primary/30 transition hover:-translate-y-0.5 hover:shadow-xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
              target="_blank"
              rel="noreferrer"
            >
              {content.hero.primaryCtaLabel}
            </Link>
          </div>
        </div>

        <div className="space-y-6">
          <div className="space-y-3 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/90 to-primary/70 p-6 text-primary-foreground shadow-lg shadow-primary/40">
            <h2 className="text-xl font-semibold">{content.reminder.headline}</h2>
            <p className="text-sm text-primary-foreground/90">{content.reminder.subheadline}</p>
          </div>
          <div className="space-y-4 rounded-2xl border border-border/60 bg-surface p-6 shadow-lg shadow-primary/10">
            <h3 className="text-lg font-semibold text-foreground">{content.lifetime.title}</h3>
            <p className="text-sm text-muted-foreground">{content.lifetime.description}</p>
            <ul className="space-y-3 text-sm text-muted-foreground">
              {content.lifetime.bullets.map((bullet) => (
                <li key={bullet} className="flex items-start gap-3 rounded-xl bg-muted/30 px-3 py-2">
                  <CheckIcon className="mt-0.5 size-4 text-primary" />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs font-medium text-muted-foreground/80">{content.lifetime.footnote}</p>
          </div>
        </div>
      </section>

      <section className="space-y-6 rounded-3xl border border-border/60 bg-surface p-10 shadow-lg shadow-primary/10">
        <h2 className="text-2xl font-semibold text-foreground">{content.benefits.title}</h2>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {content.benefits.items.map((item) => (
            <div
              key={item}
              className="flex items-start gap-3 rounded-2xl border border-border/50 bg-muted/20 p-4 text-sm text-muted-foreground"
            >
              <CheckIcon className="mt-0.5 size-4 text-primary" />
              <span>{item}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="grid gap-8 rounded-3xl border border-border/60 bg-surface/90 p-8 shadow-xl shadow-primary/10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.1fr)]">
        <div className="space-y-4">
          <h2 className="text-2xl font-semibold text-foreground">{content.video.title}</h2>
          <p className="text-sm text-muted-foreground">{content.hero.highlight}</p>
        </div>
        <div className="overflow-hidden rounded-3xl border border-border/60 bg-black/80 shadow-lg">
          <video
            controls
            poster={content.video.poster}
            className="w-full"
          >
            <source src={content.video.src} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      </section>
    </div>
  );
}
